if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector("span");

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        if (this.querySelector(".loading__spinner")) {
          this.querySelector(".loading__spinner").classList.remove("hidden");
        }

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);

        const quantityInput = evt.target
          .closest(".slide-visible")
          ?.querySelector('[name="quantity"]');
        if (quantityInput) {
          formData.set("quantity", quantityInput.value);
        }

        let cartSections = [];
        if (this.cart) {
          cartSections = this.cart.getSectionsToRender?.() || [];
          formData.append(
            "sections",
            cartSections.map((section) => section.id),
          );

          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        // Explicitly collect addon line-item properties from outside the <form> element
        // (inputs use form="..." attribute which FormData constructor may not include)
        const productInfoForAddons = this.closest("product-info");
        if (productInfoForAddons) {
          productInfoForAddons
            .querySelectorAll("[data-addon-hidden-input]")
            .forEach((input) => {
              if (!input.name) return;
              if (input.value && input.value.trim()) {
                formData.set(input.name, input.value.trim());
              } else {
                formData.delete(input.name);
              }
            });
        }

        // Always submit main product as FormData — add-ons follow in a separate request
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");

              if (this.cart) {
                this.cart.renderContents(response);

                this.updateBlocksOnError();
              }

              if (!soldOutMessage) return;

              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker(
              "add:wait-for-subscribers",
            );

            // Submit selected add-on variants as a separate silent request after main product succeeds
            const activeAddonVariantIds = this.getActiveAddonVariantIds();
            const addonPromise = activeAddonVariantIds.length
              ? this.submitAddons(
                  activeAddonVariantIds,
                  formData.get("id"),
                  cartSections,
                )
              : Promise.resolve(null);

            if (!this.error) {
              Promise.resolve(
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: "product-form",
                  productVariantId: formData.get("id"),
                  cartData: response,
                }),
              ).then(() => {
                CartPerformance.measureFromMarker(
                  "add:wait-for-subscribers",
                  startMarker,
                );
              });
            }

            this.error = false;

            if (!this.cart) {
              addonPromise.finally(() => {
                window.location = window.routes.cart_url;
              });
              return;
            }

            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    CartPerformance.measure(
                      "add:paint-updated-sections",
                      () => {
                        this.cart.renderContents(response);
                      },
                    );
                  });
                },
                {
                  once: true,
                },
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            if (this.querySelector(".loading__spinner")) {
              this.querySelector(".loading__spinner").classList.add("hidden");
            }

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      async updateBlocksOnError() {
        const response = await fetch(`${window.shop_url}`);
        const text = await response.text();

        const parser = new DOMParser();
        const newDocument = parser.parseFromString(text, "text/html");

        this.getSectionsToRender().forEach((section) => {
          let newContent;
          let currentElement;

          if (section.id && section.selector) {
            const sectionElement = newDocument.getElementById(section.id);
            if (sectionElement) {
              newContent = sectionElement.querySelector(section.selector);
              currentElement = document
                .getElementById(section.id)
                ?.querySelector(section.selector);
              if (newContent && currentElement) {
                currentElement.innerHTML = newContent.innerHTML;
              }
            }
          } else if (section.id) {
            newContent = newDocument.getElementById(section.id);
            currentElement = document.getElementById(section.id);
            if (newContent && currentElement) {
              currentElement.innerHTML = newContent.innerHTML;
            }
          } else if (section.selector) {
            newContent = newDocument.querySelector(section.selector);
            currentElement = document.querySelector(section.selector);
            if (newContent && currentElement) {
              currentElement.innerHTML = newContent.innerHTML;
            }
          }
        });
      }

      getSectionsToRender() {
        return [
          {
            id: "CartDrawer",
            section: "cart-drawer",
            selector: ".drawer__inner",
          },
          {
            id: "cart-icon-bubble",
          },
        ];
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message",
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }

      submitAddons(addonVariantIds, mainVariantId, cartSections = []) {
        const quantity =
          parseInt(
            this.form.querySelector("[name=quantity]")?.value || "1",
            10,
          ) || 1;

        const items = addonVariantIds.map((addonVariantId) => ({
          id: addonVariantId,
          quantity,
          properties: {
            _addon_for: String(mainVariantId),
          },
        }));

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        config.headers["Content-Type"] = "application/json";

        const payload = { items };
        if (Array.isArray(cartSections) && cartSections.length) {
          payload.sections = cartSections.map((section) => section.id);
          payload.sections_url = window.location.pathname;
        }

        config.body = JSON.stringify(payload);

        return fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              console.warn("[add-ons] Cart add failed:", response.description);
              return response;
            }
            if (this.cart) {
              this.cart.renderContents(response);
            }
            return response;
          })
          .catch((e) => {
            console.error("[add-ons] Submission error:", e);
            return null;
          });
      }

      getActiveAddonVariantIds() {
        if (this.dataset.lineItemAddons !== "true") return [];

        try {
          const parsed = JSON.parse(this.dataset.activeAddonVariantIds || "[]");
          if (!Array.isArray(parsed)) return [];
          return parsed
            .map((value) => parseInt(value, 10))
            .filter((value) => Number.isFinite(value) && value > 0);
        } catch (error) {
          return [];
        }
      }
    },
  );
}
